import { styled } from '@linaria/react';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Checkbox, FormControlLabel, IconButton, Switch, Tooltip } from '@mui/material';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
    ElementaryFamilyView,
    elementaryEnabledQuery,
    elementaryFamiliesQuery,
    elementaryOpenQuery,
} from '../../../../core/mapV2/queries';
import {
    elementaryChildToggled,
    elementaryEnabledSet,
    elementaryFamilyExpandedToggled,
    elementaryFamilyToggled,
    elementarySectionToggled,
} from '../../../../core/mapV2/state';
import theme from '../../../../design/theme';
import { useAppDispatch, useAppSelector } from '../../../../types';

const Section = styled.section`
    margin: 0 12px 8px;
    border: 1px solid ${theme.color.grey.medium};
    border-radius: 8px;
    background-color: ${theme.color.white.main};
    overflow: hidden;
`;

const Head = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px 4px 4px;
`;

const HeadTitle = styled.button`
    flex: 1;
    display: flex;
    align-items: center;
    gap: 4px;
    min-width: 0;
    border: 0;
    background: transparent;
    padding: 0;
    cursor: pointer;
    font: inherit;
    text-align: left;
    color: ${theme.color.grey.dark};
`;

const Title = styled.span`
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
`;

// While the master switch is off the list stays visible but inert, as in the maquette.
const List = styled.div`
    display: flex;
    flex-direction: column;
    padding: 0 8px 8px;

    &[data-enabled='false'] {
        opacity: 0.45;
        pointer-events: none;
    }
`;

const FamilyRow = styled.div`
    display: flex;
    align-items: center;
    min-height: 32px;
`;

const Spacer = styled.span`
    flex: 0 0 28px;
`;

const FamilyLabel = styled.span`
    flex: 1;
    min-width: 0;
    font-size: 13px;
    color: ${theme.color.grey.dark};
`;

const Note = styled.span`
    display: block;
    margin: -4px 0 4px 64px;
    font-size: 11px;
    color: ${theme.color.grey.light};
`;

const Children = styled.div`
    display: flex;
    flex-direction: column;
    padding-left: 56px;
`;

const AllLink = styled.button`
    border: 0;
    background: transparent;
    padding: 0 4px;
    cursor: pointer;
    font: inherit;
    font-size: 11.5px;
    color: ${theme.color.blue.primary};
`;

const Footer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 4px;
    padding: 8px 8px 0 4px;
    border-top: 1px solid ${theme.color.grey.medium2};
    font-size: 12px;
    color: ${theme.color.grey.light};
    cursor: not-allowed;
`;

type FamilyProps = { family: ElementaryFamilyView };

const Family: FC<FamilyProps> = ({ family }) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const allOn = family.checkState === 'checked';
    let allLabel = t(allOn ? 'mapV2.elementary.hideAll' : 'mapV2.elementary.selectAll');
    if (!family.expandable) {
        allLabel = t(allOn ? 'mapV2.elementary.hide' : 'mapV2.elementary.show');
    }

    return (
        <div data-testid={`map-v2-elementary-${family.key}`}>
            <FamilyRow>
                {family.expandable ? (
                    <IconButton
                        size="small"
                        aria-label={t(
                            family.expanded ? 'mapV2.elementary.collapseFamily' : 'mapV2.elementary.expandFamily',
                        )}
                        aria-expanded={family.expanded}
                        onClick={() => dispatch(elementaryFamilyExpandedToggled(family.key))}
                    >
                        {family.expanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                    </IconButton>
                ) : (
                    <Spacer />
                )}
                <Checkbox
                    size="small"
                    checked={family.checkState === 'checked'}
                    indeterminate={family.checkState === 'indeterminate'}
                    inputProps={{ 'aria-label': family.label }}
                    onChange={() => dispatch(elementaryFamilyToggled(family.key))}
                />
                <FamilyLabel>{family.label}</FamilyLabel>
                <AllLink type="button" onClick={() => dispatch(elementaryFamilyToggled(family.key))}>
                    {allLabel}
                </AllLink>
            </FamilyRow>
            {family.key === 'steps' ? <Note>{t('mapV2.elementary.inheritsSessions')}</Note> : null}
            {family.expanded ? (
                <Children>
                    {family.children.map((child) => (
                        <FormControlLabel
                            key={child.label}
                            label={child.label}
                            slotProps={{ typography: { fontSize: 12.5 } }}
                            control={
                                <Checkbox
                                    size="small"
                                    checked={child.checked}
                                    onChange={() =>
                                        dispatch(elementaryChildToggled({ family: family.key, child: child.label }))
                                    }
                                />
                            }
                        />
                    ))}
                </Children>
            ) : null}
        </div>
    );
};

// "Données élémentaires": which point-level objects (steps, degradations, events, signage) are shown
// on the map. In this step it only holds state: the markers themselves arrive with the sessions phase.
export const ElementaryDataSection: FC = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const open = useAppSelector(elementaryOpenQuery);
    const enabled = useAppSelector(elementaryEnabledQuery);
    const families = useAppSelector(elementaryFamiliesQuery);

    return (
        <Section data-testid="map-v2-elementary">
            <Head>
                <HeadTitle
                    type="button"
                    aria-expanded={open}
                    title={t(open ? 'mapV2.elementary.collapseSection' : 'mapV2.elementary.expandSection')}
                    onClick={() => dispatch(elementarySectionToggled())}
                >
                    {open ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                    <Title>{t('mapV2.elementary.title')}</Title>
                </HeadTitle>
                <Switch
                    size="small"
                    checked={enabled}
                    inputProps={{ 'aria-label': t('mapV2.elementary.master') }}
                    onChange={(_, checked) => dispatch(elementaryEnabledSet(checked))}
                />
            </Head>
            {open ? (
                <List data-enabled={enabled}>
                    {families.map((family) => (
                        <Family key={family.key} family={family} />
                    ))}
                    <Tooltip title={t('mapV2.elementary.comingSoon')} placement="top">
                        <Footer aria-disabled="true">{t('mapV2.elementary.limitToSelection')}</Footer>
                    </Tooltip>
                </List>
            ) : null}
        </Section>
    );
};
